import { bugsnag } from 'shared/utils/services/bugsnag';
import { getMParticleDeviceId } from 'tracking/utils/getMparticleDeviceId';
import equals from 'lodash/isEqual';
import customStore from 'caches/utils/localStorage';
import { EXPERIMENT_STORE } from 'experiments/utils/constants';
import assignClient from 'experiments/services/assignClient';
import { formatExperimentGroups, getCurrentActiveExperiments } from 'experiments/utils';
import { IAssignedExperiments } from 'experiments/utils/types';
import { useEffect, useState } from 'react';
import { FeatureFlags } from 'featureFlags/constants';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';

export function useExperiment() {
  const allowExperiments = useFeatureFlag(FeatureFlags.WEB_ALLOW_EXPERIMENTS);
  const [hasExperimentGroups, setHasExperimentGroups] = useState(false);

  // This useeffect allows the app to compute experiment groups before the page is shown
  // ideally we do not want to display anything until experiment groups have been assigned
  // otherwise we are going to experience FOUC (Flash of Unstyled Content) or some elements inthe page may be displayed
  // before the device has been assigned groups
  useEffect(() => {
    try {
      if (allowExperiments) {
        const currentClientId = customStore.getItem(EXPERIMENT_STORE.CLIENT_ID);
        const currentDeviceId = getMParticleDeviceId();

        // if the client was assigned a deviceId by mparticle proceed
        // TODO: get this unique ID from the backend instead of relying on a 3rd party
        if (currentDeviceId) {
          const cachedActiveExperiments = getCurrentActiveExperiments();
          const assignedExperiments: IAssignedExperiments = customStore.getItem(
            EXPERIMENT_STORE.ASSIGNED_EXPERIMENTS,
          );

          // we must make a new assign call
          // 1. if there there are no client ID assigned to this device
          // or if the current device ID differ from the one in the localstorage
          const shouldUpdateClient = !currentClientId || currentClientId !== currentDeviceId;
          // 2. if the server has served us active experiments
          // or if cookies and current client assigned experiments in local storage do not match
          const hasCachedActiveExperiments = cachedActiveExperiments?.length > 0;

          const hasMatchingAssignedExperiments = equals(
            Object.keys(assignedExperiments || {}),
            cachedActiveExperiments.map((item) => ({ name: item.name })),
          );

          const shouldAssignExperiments =
            hasCachedActiveExperiments && (shouldUpdateClient || !hasMatchingAssignedExperiments);

          if (shouldAssignExperiments) {
            assignClient(currentDeviceId)
              .then((experimentGroups) => {
                // format raw experiment groups from the server
                // then save those in the storage
                const formattedGroups = formatExperimentGroups(
                  experimentGroups?.experimentGroupsList,
                );

                // update client id
                // update assigned experiments to new groups assignments
                // we might need to rely on the cookies as well for this one
                // TODO: The risk is having the localstorage at full capacity

                customStore.setItem(EXPERIMENT_STORE.ASSIGNED_EXPERIMENTS, formattedGroups);

                if (shouldUpdateClient) {
                  customStore.setItem(EXPERIMENT_STORE.CLIENT_ID, currentDeviceId);
                  // reset logged experiment if deviceId has changed or is newly assigned
                  customStore.setItem(EXPERIMENT_STORE.LOGGED_EXPERIMENTS, {});
                }
              })
              .catch((error) => {
                bugsnag(error, { context: 'Fetch experiment group list' });
              });
          }
        }
      }
    } catch (error) {
      bugsnag(error, { context: 'Assign experiments groups' });
    } finally {
      setHasExperimentGroups(true);
    }

    // we only need this to run once since we are not doing live updates
  }, [allowExperiments]);

  return {
    hasExperimentGroups,
  };
}
